import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Refresh,
  Delete,
  ReplayCircleFilledOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";
import { GridDeleteIcon } from "@mui/x-data-grid";
import { useState } from "react";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = `${APP_API_V1_BASE}`;
dayjs.extend(relativeTime);

export default function PendingInvitesTable({ rows, setRows }) {
  const { activeOrg, permissions } = useAuth();
  const token = localStorage.getItem("authToken");

  const headers = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const resend = async (inviteId) => {
    await axios.post(`${API_BASE}/invites/${inviteId}/resend`, {}, { headers });
  };

  const revoke = async (inviteId) => {
    await axios.delete(`${API_BASE}/invites/${inviteId}`, { headers });

    setRows((prev) => prev.filter((i) => i.id !== inviteId));
  };

  const columns = [
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 220,
    },
    {
      field: "role",
      headerName: "Role",
      minWidth: 140,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="warning" />
      ),
    },
    {
      field: "created_at",
      headerName: "Invited At",
      minWidth: 180,
      renderCell: ({ value }) =>
        value ? new Date(value).toLocaleString() : "-",
    },
    {
      field: "expires_at",
      headerName: "Expires",
      flex: 1,
      minWidth: 180,
      renderCell: (p) => {
        const expires = dayjs(p.value);
        const expired = expires.isBefore(dayjs());

        return (
          <span fontSize={13} color={expired ? "error" : "text.secondary"}>
            {expired ? "Expired" : `Expires ${expires.fromNow()}`}
          </span>
        );
      },
    },

    // ✅ ACTIONS COLUMN — CONDITIONAL
    can(permissions, "organization.invite.resend") ||
    can(permissions, "organization.invite.revoke")
      ? {
          field: "actions",
          headerName: "Actions",
          width: 140,
          sortable: false,
          filterable: false,
          renderCell: (p) => {
            const expired = dayjs(p.row.expires_at).isBefore(dayjs());

            return (
              <Box sx={{ display: "flex", gap: 1 }}>
                {can(permissions, "organization.invite.resend") && (
                  <Tooltip
                    title={expired ? "Resend invite" : "Invite still valid"}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => resend(p.row)}
                        disabled={!expired}
                      >
                        <RefreshOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {can(permissions, "organization.invite.revoke") && (
                  <Tooltip title="Revoke invite">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => revoke(p.row.id)}
                    >
                      <GridDeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          },
        }
      : null,
  ].filter(Boolean); // 👈 IMPORTANT

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  return (
    <>
      {/* <DataGrid
        rows={rows}
        getRowId={(r) => r.email}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
      /> */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}
      >
        <Table>
          {/* ✅ HEADER */}
          <TableHead>
            <TableRow
              sx={{
                background:
                  "linear-gradient(135deg, #0f172a, #0f766e 60%, #082f49)",
              }}
            >
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Email
              </TableCell>
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Role
              </TableCell>
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Status
              </TableCell>
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Invited At
              </TableCell>
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Expires
              </TableCell>

              {(can(permissions, "organization.invite.resend") ||
                can(permissions, "organization.invite.revoke")) && (
                <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          {/* ✅ BODY */}
          <TableBody>
            {paginatedRows.map((row) => {
              const expires = dayjs(row.expires_at);
              const expired = expires.isBefore(dayjs());

              return (
                <TableRow
                  key={row.email}
                  sx={{
                    backgroundColor: "#F8FAFC",
                    transition: "0.2s",
                    "&:hover": {
                      backgroundColor: "#E2E8F0",
                    },
                  }}
                >
                  {/* EMAIL */}
                  <TableCell>{row.email}</TableCell>

                  {/* ROLE */}
                  <TableCell>
                    <Chip label={row.role} size="small" variant="outlined" />
                  </TableCell>

                  {/* STATUS */}
                  <TableCell>
                    <Chip label={row.status} size="small" color="warning" />
                  </TableCell>

                  {/* CREATED */}
                  <TableCell>
                    {row.created_at
                      ? new Date(row.created_at).toLocaleString()
                      : "-"}
                  </TableCell>

                  {/* EXPIRES */}
                  <TableCell>
                    <Typography
                      fontSize={13}
                      color={expired ? "error" : "text.secondary"}
                    >
                      {expired ? "Expired" : `Expires ${expires.fromNow()}`}
                    </Typography>
                  </TableCell>

                  {/* ACTIONS */}
                  {(can(permissions, "organization.invite.resend") ||
                    can(permissions, "organization.invite.revoke")) && (
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {can(permissions, "organization.invite.resend") && (
                          <Tooltip
                            title={
                              expired ? "Resend invite" : "Invite still valid"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => resend(row)}
                                disabled={!expired}
                              >
                                <RefreshOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {can(permissions, "organization.invite.revoke") && (
                          <Tooltip title="Revoke invite">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => revoke(row.id)}
                            >
                              <GridDeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ✅ PAGINATION */}
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{
          borderTop: "1px solid #E2E8F0",
          backgroundColor: "#F8FAFC",

          "& .MuiTablePagination-toolbar": {
            px: 2,
          },
        }}
      />
    </>
  );
}
